import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { AoSGameState } from '../../types';
import { buildTrack } from '../moves/buildTrack';
import { passBuildTrack, updateBuildCostInfo } from '../moves/passBuildTrack';
import { cancelAllBuildTrack } from '../moves/cancelAllBuildTrack';
import { buildSpecialLink } from '../moves/buildSpecialLink';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 4: 線路建設フェーズ
 *
 * @see docs/specifications/logic/common/phase_04_build_track.md
 */
export const buildTrackPhase: PhaseConfig<AoSGameState> = {
  moves: {
    buildTrack,
    passBuildTrack,
    cancelAllBuildTrack,
    buildSpecialLink,  // フェリーリンク/隣接リンク敷設（マップに存在する場合のみ使用可能）
    undoToTurnStart,
  },

  turn: {
    // プレイヤーごとに手番を回す
    minMoves: 0,  // 最小Move数（0 = パス可能）
    maxMoves: 4   // 最大Move数（Engineerの場合）
  },

  onBegin: ({ G, ctx }: { G: AoSGameState; ctx: Ctx }) => {
    debug.log('[buildTrackPhase] Phase 4: Build Track started');

    // 未完成区間の所有権チェック
    G.unfinishedSections = G.unfinishedSections.filter(section => {
      if (section.tilesAddedSinceLastTurn === 0 && section.owner === ctx.currentPlayer) {
        // 所有権喪失
        debug.log(`[buildTrackPhase] Section ${section.id} ownership lost`);
        return false;
      }

      // カウンターリセット
      section.tilesAddedSinceLastTurn = 0;
      return true;
    });

    // Issue #22: Cancel操作用にスナップショットを保存
    // 深いコピーを作成してフェーズ開始時の状態を保存
    G.buildTrackPhaseSnapshot = {
      completedLinks: JSON.parse(JSON.stringify(G.completedLinks)),
      unfinishedSections: JSON.parse(JSON.stringify(G.unfinishedSections)),
      // 特殊リンク所有権状態（Cancel復元用）
      ferryLinkOwners: G.ferryLinks?.map(l => ({ id: l.id, owner: l.owner })) || [],
      adjacentLinkOwners: G.adjacentLinks?.map(l => ({ id: l.id, owner: l.owner })) || [],
    };
    debug.log(`[buildTrackPhase] Snapshot saved: ${G.buildTrackPhaseSnapshot.completedLinks.length} links, ${G.buildTrackPhaseSnapshot.unfinishedSections.length} sections, ferry: ${G.buildTrackPhaseSnapshot.ferryLinkOwners?.length || 0}, adjacent: ${G.buildTrackPhaseSnapshot.adjacentLinkOwners?.length || 0}`);

    // プレイヤーごとの配置枚数リセット
    Object.values(G.players).forEach(player => {
      player.tilesPlacedThisTurn = 0;
      player.newCityTilesPlacedThisTurn = 0;
      player.hasPassedBuild = false;
      player.buildTrackHistory = []; // Cancel操作用の履歴をクリア
      // 重要: selectedActionはこのPhaseで使用するため、ここではクリアしない
    });

    // 建設コスト情報を初期化（UI表示用）
    updateBuildCostInfo(G, ctx.currentPlayer);
  },

  onEnd: ({ G }: { G: AoSGameState }) => {
    debug.log('[buildTrackPhase] Phase 4 ended');
    // 重要: selectedActionはPhase 5-9でも使用される（PRODUCTION選択時など）
    // リセットはPhase 10（ターン境界）で実施

    // Phase 5 用に hasPassed をリセット（Phase 3 で true になっている可能性がある）
    Object.values(G.players).forEach(player => {
      player.hasPassed = false;
    });
  },

  endIf: ({ G }: { G: AoSGameState; ctx: Ctx }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[buildTrackPhase] Force transition to issueShares (Undo)');
      return true;
    }

    // 全プレイヤーが手動で「敷設完了」を押したかチェック
    const allPlayersCompleted = Object.values(G.players).every(player => {
      if (player.isBankrupt) return true;

      // 修正: タイル上限到達での自動終了を廃止し、hasPassedBuildのみで判定
      return player.hasPassedBuild;
    });

    if (allPlayersCompleted) {
      debug.log('[buildTrackPhase] All players completed, ending phase');
      return true;
    }

    return false;
  },

  // Phase 5 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: AoSGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'moveGoods';
  }
};
